import { MantineColor, rgba, rem, MantineTheme } from '@mantine/core';
import { CSSProperties } from 'react';

type ColorScheme = 'light' | 'dark';

export const METRIC_CARD_PAPER_CONSTANTS: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  backdropFilter: 'blur(18px) saturate(115%)',
  WebkitBackdropFilter: 'blur(18px) saturate(115%)',
  transition: 'transform 140ms ease, box-shadow 180ms ease',
};

export const METRIC_CARD_TOKENS = {
  icon: { sizePx: 36, radiusPx: 12 },
  topEdge: { heightPx: 1 },
  gradientStops: { start: '0%', s1: '15%', s2: '28%', s3: '62%', end: '100%' },
} as const;

export const getAccentScale = (theme: MantineTheme, accent: MantineColor) => {
  const scale = theme.colors?.[accent] || theme.colors.blue;
  return {
    nearWhite: scale?.[1] ?? '#e6f4ff', // c1
    light: scale?.[3] ?? '#a3d4ff', // c3
    mid: scale?.[5] ?? '#5ab0ff', // c5
    strong: scale?.[6] ?? (accent === 'green' ? '#22c55e' : '#3b82f6'), // c6
    darkest: scale?.[7] ?? (accent === 'green' ? '#16a34a' : '#2563eb'), // c7
  };
};

export const getCardStyles = (scheme: ColorScheme) => {
  const lightModeFactor = 0.75;

  const mainA1 = scheme === 'dark' ? 0.22 : 0.42 * lightModeFactor;
  const mainA2 = scheme === 'dark' ? 0.26 : 0.5 * lightModeFactor;
  const mainA3 = scheme === 'dark' ? 0.3 : 0.58 * lightModeFactor;

  const haloAlpha = scheme === 'dark' ? 0.18 : 0.16;
  const iconBgAlpha = scheme === 'dark' ? 0.22 : 0.18;
  const iconRingAlpha = scheme === 'dark' ? 0.28 : 0.22;

  const surfaceFill =
    scheme === 'dark' ? 'rgba(10,12,16,0.42)' : 'rgba(255,255,255,0.70)';
  const borderColor =
    scheme === 'dark' ? rgba('#FFFFFF', 0.1) : rgba('#0A0A0A', 0.1);
  const textColor = scheme === 'dark' ? 'white' : 'black';

  const restingShadow =
    scheme === 'dark'
      ? '0 4px 12px rgba(0,0,0,0.28), 0 12px 22px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.06)'
      : '0 4px 10px rgba(0,0,0,0.12), 0 14px 26px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.35)';

  const topEdgeBackground =
    scheme === 'dark'
      ? 'linear-gradient(to right, rgba(255,255,255,0.06), rgba(255,255,255,0.02))'
      : 'linear-gradient(to right, rgba(255,255,255,0.85), rgba(255,255,255,0.25))';

  const topHighlight =
    scheme === 'dark'
      ? 'linear-gradient(to bottom, rgba(255,255,255,0.05), rgba(255,255,255,0.00) 18%)'
      : 'linear-gradient(to bottom, rgba(255,255,255,0.28), rgba(255,255,255,0.00) 18%)';

  return {
    // gradient alphas
    mainA1,
    mainA2,
    mainA3,
    // glow/halo alphas
    haloAlpha,
    iconBgAlpha,
    iconRingAlpha,
    // colors & fills
    surfaceFill,
    borderColor,
    textColor,
    restingShadow,
    topHighlight,
    topEdgeBackground,
  };
};

export const getPaperStyles = (
  theme: MantineTheme,
  accent: MantineColor,
  scheme: ColorScheme
) => {
  const stops = METRIC_CARD_TOKENS.gradientStops;
  const scale = getAccentScale(theme, accent);

  const cardStyles = getCardStyles(scheme);

  const accentGradient = `linear-gradient(
    135deg,
    ${rgba(scale.darkest, cardStyles.mainA3)} ${stops.start},
    ${rgba(scale.strong, cardStyles.mainA3)} ${stops.s1},
    ${rgba(scale.mid, cardStyles.mainA3)} ${stops.s2},
    ${rgba(scale.light, cardStyles.mainA2)} ${stops.s3},
    ${rgba(scale.nearWhite, cardStyles.mainA1)} ${stops.end}
  )`;

  const cornerHalo = `radial-gradient(1100px 260px at 0% 0%,
    ${rgba(scale.darkest, cardStyles.haloAlpha)},
    rgba(0,0,0,0) 60%
  )`;

  const backgroundImage = [
    cornerHalo,
    cardStyles.topHighlight,
    accentGradient,
  ].join(', ');
  const backgroundBlendMode = 'overlay, normal, normal';
  const backgroundSize = '100% 100%, 100% 100%, 100% 100%';
  const backgroundPosition = '0 0, 0 0, 0 0';

  return {
    backgroundImage,
    backgroundBlendMode,
    backgroundSize,
    backgroundPosition,
    borderColor: cardStyles.borderColor,
    boxShadow: cardStyles.restingShadow,
    color: cardStyles.textColor,
    backdropFilter: METRIC_CARD_PAPER_CONSTANTS.backdropFilter,
    WebkitBackdropFilter: METRIC_CARD_PAPER_CONSTANTS.WebkitBackdropFilter,
  };
};

export const getIconBadgeStyles = (
  theme: MantineTheme,
  accent: MantineColor,
  scheme: ColorScheme
) => {
  const scale = getAccentScale(theme, accent);
  const cardStyles = getCardStyles(scheme);

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: rem(METRIC_CARD_TOKENS.icon.sizePx),
    height: rem(METRIC_CARD_TOKENS.icon.sizePx),
    borderRadius: rem(METRIC_CARD_TOKENS.icon.radiusPx),
    background: rgba(scale.strong, cardStyles.iconBgAlpha),
    boxShadow: `inset 0 0 0 1px ${rgba(scale.mid, cardStyles.iconRingAlpha)}`,
  };
};

export const getTopEdgeStyles = (scheme: ColorScheme): CSSProperties => {
  const cardStyles = getCardStyles(scheme);
  return {
    position: 'absolute',
    insetInline: 0,
    top: 0,
    height: rem(METRIC_CARD_TOKENS.topEdge.heightPx),
    background: cardStyles.topEdgeBackground,
    pointerEvents: 'none',
  };
};
